import { useForm } from 'react-hook-form';
import { UnitOfMeasureType, UpdateApplicationSettingsDto } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { connect, useDispatch } from 'react-redux';
import { saveGeneralAppSettings } from './redux/settingsSlices';
import { Button, Grid, MenuItem } from '@mui/material';
import { FormCheckbox, FormSelect } from '../../common/form';
import { useEffect } from 'react';

const mapStateToProps = (state: RootState) => ({
    settings: state.settings.app
});

type Props = ReturnType<typeof mapStateToProps>;

const schema: yup.SchemaOf<UpdateApplicationSettingsDto> = yup.object().shape({
    defaultCurrency: yup.string().optional().label('Currency'),
    language: yup.string().optional().label('Language'),
    unitOfMeasure: yup.number().optional().label('Unit of measure'),
    inverseExchangeRates: yup.bool().optional().label('Inverse exchange rates')
});

const GeneralApplicationSettings = ({ settings }: Props) => {
    const dispatch = useDispatch();
    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, isValid }
    } = useForm<UpdateApplicationSettingsDto>({
        defaultValues: {
            defaultCurrency: settings.defaultCurrency ?? '',
            language: settings.language ?? '',
            unitOfMeasure: settings.unitOfMeasure,
            inverseExchangeRates: settings.inverseExchangeRates
        },
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        reset({
            defaultCurrency: settings.defaultCurrency,
            language: settings.language,
            unitOfMeasure: settings.unitOfMeasure,
            inverseExchangeRates: settings.inverseExchangeRates
        });
    }, [reset, settings]);

    const submit = (dto: UpdateApplicationSettingsDto) => {
        dispatch(saveGeneralAppSettings.request(dto));
    };

    return (
        <form id="agreement-form" onSubmit={handleSubmit(submit)}>
            <Grid container direction="column" rowGap={4} sx={{ width: 250 }}>
                <Grid item>
                    <FormSelect label="Unit of measure" name="unitOfMeasure" control={control}>
                        {Object.keys(UnitOfMeasureType)
                            .map(x => Number(x))
                            .filter(x => !isNaN(x))
                            .map(idx => [idx, UnitOfMeasureType[idx]] as const)
                            .map(([idx, name]) => (
                                <MenuItem key={idx} value={idx} sx={{ width: 'fit-content' }}>
                                    {name}
                                </MenuItem>
                            ))}
                    </FormSelect>
                </Grid>

                <Grid item>
                    <FormSelect label="Language" name="language" control={control}>
                        {settings.availableLanguages.map(language => (
                            <MenuItem key={language} value={language}>
                                {language}
                            </MenuItem>
                        ))}
                    </FormSelect>
                </Grid>

                <Grid item>
                    <FormSelect label="Default currency" name="defaultCurrency" control={control}>
                        {settings.availableCurrencies.map(currency => (
                            <MenuItem key={currency} value={currency}>
                                {currency}
                            </MenuItem>
                        ))}
                    </FormSelect>
                </Grid>

                <Grid item>
                    <FormCheckbox
                        name="inverseExchangeRates"
                        label="Inverse exchange rates"
                        control={control}
                    />
                </Grid>

                <Grid item>
                    <Button type="submit" color="primary" variant="contained" disabled={!isDirty || !isValid}>
                        Save
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

export default connect(mapStateToProps)(GeneralApplicationSettings);
